import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useCatalog } from '../../context/CatalogContext'
import { saveTenantSettings } from '../../services/catalog/supabaseCatalog'
import { bridgeVersion, getNetworkPrint, scanPrinters, setNetworkPrint } from '../../services/printBridge'
import { printTestPage } from '../../services/networkPrint'
import { downloadKioskSetup } from '../../services/kioskSetup'
import type { NetworkPrinter } from '../../types'
import { stationNames } from '../../utils/stations'
import { CheckIcon, DownloadIcon, ExternalLinkIcon, PencilIcon, PlugConnectedIcon, PlusIcon, PrinterIcon, TrashIcon } from '../icons'
import './PrintersPanel.css'

/** Yerel ağ adresi mi (10.x, 172.16-31.x, 192.168.x); köprü de yalnız bunları kabul ediyor. */
export function isLocalIp(ip: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip.trim())
  if (!m) return false
  const [a, b, c, d] = m.slice(1).map(Number)
  if ([a, b, c, d].some((n) => n > 255)) return false
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168)
}

const blank = (): NetworkPrinter => ({
  id: crypto.randomUUID(),
  name: '',
  host: '',
  port: 9100,
  paper: 80,
  cash: false,
  stations: [],
  others: false,
})

/**
 * Ayarlar > Yazıcılar (20 Eki 2026): Ethernet'li fiş yazıcıları, en fazla beş.
 * Her yazıcıya kasa fişi ve/veya tezgahlar veriliyor; garsonun siparişi
 * tezgah tezgah kendi yazıcısından çıkıyor. Basan bilgisayar, üstteki cihaz
 * ayarı açık ve Yazıcı Köprüsü kurulu olan kasa.
 */
interface Props {
  /** Ayarlar'dan gelen öteki cihaz ayarları (otomatik yazdırma, kasa numarası). */
  deviceRows?: React.ReactNode
  /**
   * Kasiyer (20 Eki 2026): yazıcı listesi kurumun, her cihazı etkiliyor; onu
   * patron düzenliyor. Kasiyer listeyi görüyor ve deneme fişi basabiliyor.
   */
  readOnly?: boolean
}

export function PrintersPanel({ deviceRows, readOnly }: Props) {
  const { tenantId, printers } = useAuth()
  const { categories, products } = useCatalog()
  const stations = stationNames(categories, products)

  const [netOn, setNetOn] = useState(getNetworkPrint)
  /** undefined: bakılıyor, null: köprü yok. */
  const [bridge, setBridge] = useState<string | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    const check = () => void bridgeVersion().then((v) => alive && setBridge(v))
    check()
    const t = window.setInterval(check, 10_000)
    return () => {
      alive = false
      window.clearInterval(t)
    }
  }, [])

  const [draft, setDraft] = useState<NetworkPrinter | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** "Ağda ara" sonucu: undefined aranmadı, null köprü cevap vermedi. */
  const [found, setFound] = useState<string[] | null | undefined>(undefined)
  const [scanning, setScanning] = useState(false)
  const onScan = async () => {
    setScanning(true)
    setFound(await scanPrinters())
    setScanning(false)
  }
  const [helpOpen, setHelpOpen] = useState(false)
  /** Deneme fişinin sonucu, yazıcı kimliğine göre. */
  const [tests, setTests] = useState<Record<string, string>>({})

  const save = async (next: NetworkPrinter[]) => {
    if (!tenantId) return
    setBusy(true)
    setError(null)
    try {
      await saveTenantSettings(tenantId, { printers: next })
      setDraft(null)
    } catch {
      setError('Kaydedilemedi. İnternet bağlantınızı kontrol edin.')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = () => {
    if (!draft) return
    const d = { ...draft, name: draft.name.trim(), host: draft.host.trim() }
    if (!d.name) return setError('Yazıcıya bir ad verin (örn. Mutfak).')
    if (!isLocalIp(d.host)) return setError('IP adresini yazın, örn. 192.168.1.50. Yazıcının ağ ayarları fişinde yazar.')
    if (!Number.isInteger(d.port) || d.port < 1 || d.port > 65535) return setError('Port çoğunlukla 9100.')
    if (!d.cash && d.stations.length === 0 && !d.others) {
      return setError('Bu yazıcı neyi bassın? Kasa fişini ya da en az bir tezgahı seçin.')
    }
    const exists = printers.some((p) => p.id === d.id)
    void save(exists ? printers.map((p) => (p.id === d.id ? d : p)) : [...printers, d])
  }

  const onTest = async (p: NetworkPrinter) => {
    setTests((t) => ({ ...t, [p.id]: 'Gönderiliyor…' }))
    try {
      await printTestPage(p)
      setTests((t) => ({ ...t, [p.id]: 'Gönderildi, yazıcıya bakın.' }))
    } catch (err) {
      setTests((t) => ({ ...t, [p.id]: err instanceof Error ? err.message : 'Gönderilemedi.' }))
    }
  }

  const toggleStation = (st: string) => {
    if (!draft) return
    const has = draft.stations.includes(st)
    setDraft({ ...draft, stations: has ? draft.stations.filter((s) => s !== st) : [...draft.stations, st] })
  }

  const summary = (p: NetworkPrinter) =>
    [p.cash && 'Kasa fişi', ...p.stations, p.others && 'Diğer tezgahlar'].filter(Boolean).join(', ')

  const openNew = () => {
    setError(null)
    setDraft(blank())
  }

  return (
    <div className="prn">
      {/* Kurulum ve köprü tek kartta, durum rozetle (20 Eki 2026): eskiden iki
          satır ve kırmızı bir cümleydi, bakınca anlaşılmıyordu. */}
      <section className={`prn-setup ${bridge ? 'is-ok' : bridge === null ? 'is-missing' : ''}`}>
        <span className="prn-setup__icon" aria-hidden="true">
          <PlugConnectedIcon />
        </span>
        <div className="prn-setup__text">
          <div className="prn-setup__title">
            <b>Yazıcı kurulumu</b>
            <span className={`prn-pill ${bridge ? 'is-ok' : bridge === null ? 'is-bad' : ''}`}>
              {bridge === undefined ? 'Bakılıyor…' : bridge ? 'Köprü çalışıyor' : 'Köprü kurulu değil'}
            </span>
          </div>
          <p>
            Kasa bilgisayarına bir kez kurulur: Adissoo kısayolu ve fişleri Ethernet yazıcılara ileten Yazıcı
            Köprüsü.
          </p>
        </div>
        <div className="prn-setup__actions">
          <button type="button" className={`btn ${bridge ? 'btn--ghost' : 'btn--primary'}`} onClick={downloadKioskSetup}>
            <DownloadIcon />
            <i className="btn__rule" />
            İndir
          </button>
          <a className="btn btn--ghost" href="https://www.adissoo.com/yazici-kurulum" target="_blank" rel="noreferrer">
            Rehber
            <ExternalLinkIcon />
          </a>
        </div>
      </section>

      <section className="prn-block">
        <h4 className="prn__group">Bu bilgisayar</h4>
        <div className="setting">
          <div>
            <div className="setting__label">Ağ yazıcılarına gönder</div>
            <div className="setting__hint">Fişler aşağıdaki yazıcılara gider. Yalnız kasa bilgisayarında açın.</div>
          </div>
          <button
            type="button"
            className={`prn__switch ${netOn ? 'is-on' : ''}`}
            role="switch"
            aria-checked={netOn}
            aria-label="Ağ yazıcılarına gönder"
            onClick={() => {
              setNetworkPrint(!netOn)
              setNetOn(!netOn)
            }}
          >
            <span />
          </button>
        </div>
        {deviceRows}
      </section>

      <section className="prn-block">
        <div className="prn-block__head">
          <h4 className="prn__group">
            Yazıcılar <span>(bütün cihazlar için)</span>
          </h4>
          {printers.length > 0 && !draft && !readOnly && (
            <button type="button" className="btn btn--ghost prn-add" onClick={openNew}>
              <PlusIcon />
              <i className="btn__rule" />
              Yazıcı ekle
            </button>
          )}
        </div>

        {printers.length === 0 && !draft && (
          <div className="prn-empty">
            <span className="prn-empty__icon" aria-hidden="true">
              <PrinterIcon />
            </span>
            <b>Henüz yazıcı yok</b>
            {readOnly ? (
              <p>Ethernet fiş yazıcılarını işletme sahibi Ayarlar › Yazıcılar'dan ekler.</p>
            ) : (
              <>
                <p>Ethernet kablosuyla modeme bağlı fiş yazıcılarını ekleyin. Her yazıcıya kasa fişi ya da tezgah verilir; istediğiniz kadar yazıcı ekleyebilirsiniz.</p>
                <button type="button" className="btn btn--primary" onClick={openNew}>
                  <PlusIcon />
                  <i className="btn__rule" />
                  Yazıcı ekle
                </button>
              </>
            )}
          </div>
        )}

        {printers.length > 0 && (
          <ul className="prn-grid">
            {printers.map((p) =>
              draft?.id === p.id ? null : (
                <li key={p.id} className="prn-card">
                  <div className="prn-card__top">
                    <span className="prn-card__icon" aria-hidden="true">
                      <PrinterIcon />
                    </span>
                    <div className="prn-card__name">
                      <b>{p.name}</b>
                      <small>
                        {p.host}, {p.paper} mm
                      </small>
                    </div>
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => {
                            setError(null)
                            setDraft(p)
                          }}
                          aria-label={`${p.name} düzenle`}
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          className="icon-btn is-danger"
                          onClick={() => {
                            if (window.confirm(`"${p.name}" yazıcısı silinsin mi?`)) {
                              void save(printers.filter((x) => x.id !== p.id))
                            }
                          }}
                          aria-label={`${p.name} sil`}
                        >
                          <TrashIcon />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="prn-card__chips">
                    {p.cash && <span className="prn-chip is-cash">Kasa fişi</span>}
                    {p.stations.map((st) => (
                      <span key={st} className="prn-chip">
                        {st}
                      </span>
                    ))}
                    {p.others && <span className="prn-chip is-muted">Diğer tezgahlar</span>}
                  </div>
                  <div className="prn-card__foot">
                    <button type="button" className="btn btn--ghost prn-test" onClick={() => void onTest(p)} disabled={!bridge}>
                      Deneme fişi
                    </button>
                    {tests[p.id] && <span className="prn-card__test">{tests[p.id]}</span>}
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
        {error && !draft && <p className="prn__err">{error}</p>}
      </section>

      {draft && (
        <div className="prn__form">
          <b className="prn__form-title">
            {printers.some((p) => p.id === draft.id) ? `${draft.name || 'Yazıcı'} düzenle` : 'Yeni yazıcı'}
          </b>

          <div className="prn__row">
            <label>
              Ad
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Mutfak"
              />
            </label>
            <div className="prn__field">
              <span>Kağıt</span>
              <div className="prn__seg">
                {([80, 58] as const).map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={draft.paper === w ? 'is-on' : ''}
                    onClick={() => setDraft({ ...draft, paper: w })}
                  >
                    {w} mm
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="prn__row prn__row--ip">
            <label>
              IP adresi
              <span className="prn__ipwrap">
                <input
                  value={draft.host}
                  onChange={(e) => setDraft({ ...draft, host: e.target.value })}
                  placeholder="192.168.1.50"
                  inputMode="decimal"
                  autoComplete="off"
                />
                {/* IP'yi bulmak (20 Eki 2026): köprü ağı tarıyor; bulunamazsa
                    yazıcının kendi ayar fişi. */}
                <button type="button" className="btn btn--ghost" onClick={() => void onScan()} disabled={!bridge || scanning}>
                  {scanning ? 'Aranıyor…' : 'Ağda ara'}
                </button>
              </span>
            </label>
            <label>
              Port
              <input
                value={draft.port}
                onChange={(e) => setDraft({ ...draft, port: Number(e.target.value.replace(/\D/g, '')) || 0 })}
                inputMode="numeric"
              />
            </label>
          </div>

          <div className="prn__find">
            {found === null && <span className="prn__err">Köprü cevap vermedi. Kurulum dosyasını yeniden çalıştırın.</span>}
            {found && found.length === 0 && <span className="setting__hint">Ağda fiş yazıcısı bulunamadı.</span>}
            {found?.map((ip) => {
              const used = printers.find((p) => p.host === ip && p.id !== draft.id)
              return (
                <button
                  key={ip}
                  type="button"
                  className={`prn__ip ${draft.host === ip ? 'is-on' : ''}`}
                  onClick={() => setDraft({ ...draft, host: ip, port: 9100 })}
                >
                  {ip}
                  {used && <small> ({used.name})</small>}
                </button>
              )
            })}
            <button type="button" className="prn__help-toggle" onClick={() => setHelpOpen(!helpOpen)}>
              IP adresi nerede yazar?
            </button>
          </div>
          {helpOpen && (
            <div className="prn__help">
              <p>
                <b>Port</b> neredeyse her fiş yazıcısında <b>9100</b>, değiştirmeyin.
              </p>
              <p>
                <b>IP adresi</b> yazıcının ayar fişinde yazar. Yazıcı kapalıyken <b>FEED</b> (kağıt ilerletme)
                düğmesini basılı tutup yazıcıyı açın, birkaç saniye sonra bırakın. Çıkan fişte “IP Address” satırı
                (örn. 192.168.1.87). Epson'larda arkadaki küçük düğmeye basmak da aynı fişi verir.
              </p>
              <p>
                Adres 192.168.123.100 ya da 192.168.192.168 gibi modemle aynı başlamıyorsa (modem çoğunlukla
                192.168.1.x) yazıcı fabrika adresinde kalmıştır: “Ağda ara” bulamaz. Yazıcının kendi ayar
                programıyla (Xprinter için “Printer Tool”, Epson için “EpsonNet Config”) DHCP'yi açın ya da modemle
                aynı başlayan bir adres verin. Adres değişmesin diye modemde bu yazıcıya sabit IP (DHCP
                rezervasyonu) tanımlayın.
              </p>
            </div>
          )}

          {/* Seçilebilir etiketler: birden çok seçilir, kartlardaki etiketlerle aynı. */}
          <div className="prn__field">
            <span>Neleri bassın</span>
            <div className="prn__opts">
              <button
                type="button"
                className={`prn__opt ${draft.cash ? 'is-on' : ''}`}
                aria-pressed={draft.cash}
                onClick={() => setDraft({ ...draft, cash: !draft.cash })}
              >
                {draft.cash && <CheckIcon />}
                Kasa fişi
              </button>
              {stations.map((st) => {
                const on = draft.stations.includes(st)
                return (
                  <button
                    key={st}
                    type="button"
                    className={`prn__opt ${on ? 'is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => toggleStation(st)}
                  >
                    {on && <CheckIcon />}
                    {st}
                  </button>
                )
              })}
              <button
                type="button"
                className={`prn__opt ${draft.others ? 'is-on' : ''}`}
                aria-pressed={draft.others}
                onClick={() => setDraft({ ...draft, others: !draft.others })}
              >
                {draft.others && <CheckIcon />}
                Diğer tezgahlar
              </button>
            </div>
            <small className="prn__opts-hint">
              Kasa fişi ödeme alınınca basılır. "Diğer tezgahlar": hiçbir yazıcıya verilmemiş tezgahların fişleri.
            </small>
          </div>

          {error && <p className="prn__err">{error}</p>}

          <div className="prn__buttons">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setDraft(null)
                setError(null)
              }}
              disabled={busy}
            >
              Vazgeç
            </button>
            <button type="button" className="btn btn--primary" onClick={onSubmit} disabled={busy}>
              {busy ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}

    </div>
  )
}
